import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import MainLayout from './MainLayout';
import { 
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon
} from '@mui/icons-material';
import SortablePaginatedTable from './common/SortablePaginatedTable';
import '../styles/Dashboard.css';

// Get the API base URL from environment variables
const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://pulse.marketersindemand.com';

export const AuditLogs = () => {
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchParams, setSearchParams] = useState({
    contractId: '',
    startDate: '',
    endDate: ''
  });

  // Fetch recent audit logs on component mount
  useEffect(() => {
    fetchRecentLogs();
  }, []);

  // Fetch the 20 most recent audit logs
  const fetchRecentLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${apiBaseUrl}/api/audit-logs?limit=20`);
      if (!response.ok) {
        throw new Error(`Failed to fetch recent audit logs: ${response.status}`);
      }

      const result = await response.json();
      
      console.log('Recent logs API response:', result);
      
      // Check if data has the expected structure (with a data property)
      if (result && result.data) {
        setRecentLogs(result.data);
      } else if (Array.isArray(result)) {
        // Handle case where data is directly an array
        setRecentLogs(result);
      } else {
        setRecentLogs([]);
      }
    } catch (error) {
      console.error('Error fetching recent audit logs:', error);
      setError(`Failed to load recent audit logs: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle search form input changes
  const handleSearchInputChange = (e) => {
    const { name, value } = e.target;
    setSearchParams(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle search submission
  const handleSearch = async (e) => {
    e.preventDefault();
    
    // Check if any filter is provided
    if (!searchParams.contractId.trim() && !searchParams.startDate && !searchParams.endDate) {
      setError('Please provide at least one search filter (Contract Number, Start Date, or End Date)');
      return;
    }

    try {
      setSearchLoading(true);
      setError(null);

      let url;
      const queryParams = new URLSearchParams();
      
      // Build the URL and query parameters
      if (searchParams.contractId.trim()) {
        // If contract ID is provided, use the /contract/:id endpoint
        url = `${apiBaseUrl}/api/audit-logs/contract/${searchParams.contractId.trim()}`;
      } else {
        // Otherwise use the date-range endpoint
        url = `${apiBaseUrl}/api/audit-logs/date-range`;
      }
      
      // Add date range parameters if provided
      if (searchParams.startDate) {
        queryParams.append('startDate', searchParams.startDate);
      }
      
      if (searchParams.endDate) {
        queryParams.append('endDate', searchParams.endDate);
      }
      
      // For date-range endpoint, we might also need pagination
      if (!searchParams.contractId.trim()) {
        queryParams.append('page', '1');
        queryParams.append('limit', '100'); // Get a decent number of results
      }
      
      // Append query params to URL if any exist
      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch audit logs: ${response.status}`);
      }

      const result = await response.json();
      console.log('Search results:', result);
      
      if (Array.isArray(result)) {
        setSearchResults(result);
        setShowSearchResults(true);
      } else if (result.data && Array.isArray(result.data)) {
        setSearchResults(result.data);
        setShowSearchResults(true);
      } else {
        setSearchResults([]);
        setShowSearchResults(true);
      }
    } catch (error) {
      console.error('Error searching audit logs:', error);
      setError(`Failed to search audit logs: ${error.message}`);
    } finally {
      setSearchLoading(false);
    }
  };

  // Reset search
  const handleResetSearch = () => {
    setSearchParams({
      contractId: '',
      startDate: '',
      endDate: ''
    });
    setSearchResults([]);
    setShowSearchResults(false);
    setError(null);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Column definitions for the audit logs table
  const auditLogsColumns = [
    { 
      id: 'id', 
      label: 'Change Number', 
      sortable: true 
    },
    { 
      id: 'contract_number', 
      label: 'Contract ID', 
      sortable: true,
      render: (row) => (
        <Link 
          to={`/contracts/${row.contract_id}`} 
          style={{ 
            color: '#6200ee', 
            textDecoration: 'underline',
            fontWeight: 500
          }}
        >
          {row.contract_number}
        </Link>
      )
    },
    { 
      id: 'contract_name', 
      label: 'Contract Name', 
      sortable: true 
    },
    { 
      id: 'description', 
      label: 'Description', 
      sortable: false 
    },
    { 
      id: 'updated_by', 
      label: 'Updated By', 
      sortable: true 
    },
    { 
      id: 'changed_at', 
      label: 'Timestamp', 
      sortable: true,
      getValue: (row) => row.changed_at ? new Date(row.changed_at).getTime() : 0,
      render: (row) => formatDate(row.changed_at)
    }
  ];

  return (
    <MainLayout title="Audit Logs">
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#2c2c2c', mb: 3 }}>
            Search Audit Logs
          </Typography>

          <Grid container spacing={3} component="form" onSubmit={handleSearch}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                label="Contract Number"
                name="contractId"
                value={searchParams.contractId}
                onChange={handleSearchInputChange}
                placeholder="Enter Contract Number (e.g. MID20240001)"
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  backgroundColor: '#fff',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#ccc',
                    },
                    '&:hover fieldset': {
                      borderColor: '#999',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#6200ee',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: '#000',
                    padding: '12px 14px',
                    height: '24px',
                    zIndex: 1,
                    position: 'relative',
                  },
                  '& .MuiInputLabel-root': {
                    color: '#666',
                    zIndex: 2
                  },
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                variant="outlined"
                label="Start Date"
                name="startDate"
                type="date"
                value={searchParams.startDate}
                onChange={handleSearchInputChange}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  backgroundColor: '#fff',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#ccc',
                    },
                    '&:hover fieldset': {
                      borderColor: '#999',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#6200ee',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: '#000',
                    padding: '12px 14px',
                    height: '24px',
                    zIndex: 1,
                    position: 'relative',
                  },
                  '& .MuiInputLabel-root': {
                    color: '#666',
                    zIndex: 2
                  },
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                variant="outlined"
                label="End Date"
                name="endDate"
                type="date"
                value={searchParams.endDate}
                onChange={handleSearchInputChange}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  backgroundColor: '#fff',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#ccc',
                    },
                    '&:hover fieldset': {
                      borderColor: '#999',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#6200ee',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: '#000',
                    padding: '12px 14px',
                    height: '24px',
                    zIndex: 1,
                    position: 'relative',
                  },
                  '& .MuiInputLabel-root': {
                    color: '#666',
                    zIndex: 2
                  },
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={searchLoading}
                sx={{
                  height: '48px',
                  backgroundColor: '#6200ee',
                  '&:hover': {
                    backgroundColor: '#5000c1',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#e0e0e0',
                    color: '#999',
                  },
                }}
              >
                {searchLoading ? (
                  <CircularProgress size={24} sx={{ color: '#fff' }} />
                ) : (
                  <>
                    <SearchIcon sx={{ mr: 1 }} />
                    Find
                  </>
                )}
              </Button>
            </Grid>
          </Grid>
          
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mt: 2,
                backgroundColor: '#ffebee',
                border: '1px solid #f44336',
                '& .MuiAlert-icon': {
                  color: '#d32f2f',
                },
                '& .MuiAlert-message': {
                  color: '#d32f2f',
                  fontWeight: 500,
                },
              }}
            >
              {error}
            </Alert>
          )}

          {showSearchResults && searchResults.length === 0 && !searchLoading && (
            <Alert 
              severity="info"
              sx={{ 
                mt: 2,
                backgroundColor: '#e3f2fd',
                border: '1px solid #2196f3',
                '& .MuiAlert-icon': {
                  color: '#0288d1',
                },
                '& .MuiAlert-message': {
                  color: '#0288d1',
                  fontWeight: 500,
                },
              }}
            >
              No audit logs found for the given contract number.
            </Alert>
          )}
        </Paper>
        
        {/* Search Results Table */}
        {showSearchResults && searchResults.length > 0 && (
          <Paper sx={{ p: 3, mb: 4 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 2 
            }}>
              <Typography variant="h6" sx={{ color: '#2c2c2c' }}>
                Search Results
              </Typography>
              <Button 
                variant="outlined" 
                onClick={handleResetSearch}
                sx={{
                  borderColor: '#6200ee',
                  color: '#6200ee',
                  '&:hover': {
                    borderColor: '#5000c1',
                    backgroundColor: 'rgba(98, 0, 238, 0.04)',
                  },
                }}
              >
                Clear Results
              </Button>
            </Box>
            
            <SortablePaginatedTable
              data={searchResults}
              columns={auditLogsColumns}
              rowsPerPage={10}
              emptyMessage="No audit logs found"
              tableContainerProps={{ 
                sx: {
                  '& .MuiTableCell-root': {
                    color: '#2c2c2c',
                  },
                  '& .MuiTableCell-head': {
                    fontWeight: 600,
                    backgroundColor: '#f5f5f5',
                  },
                }
              }}
              isContractDetailTable={false}
            />
          </Paper>
        )}
        
        {/* Recent Audit Logs Table */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#2c2c2c', mb: 3 }}>
            Recent Contract Changes
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress sx={{ color: '#6200ee' }} />
            </Box>
          ) : (
            <SortablePaginatedTable
              data={recentLogs}
              columns={auditLogsColumns}
              rowsPerPage={10}
              emptyMessage="No recent audit logs found"
              tableContainerProps={{ 
                sx: {
                  '& .MuiTableCell-root': {
                    color: '#2c2c2c',
                  },
                  '& .MuiTableCell-head': {
                    fontWeight: 600,
                    backgroundColor: '#f5f5f5',
                  },
                }
              }}
              isContractDetailTable={false}
            />
          )}
        </Paper>
      </Box>
    </MainLayout>
  );
}; 